import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useWallLiveState from './useWallLiveState';
import { useWallTimeline } from './useWallTimeline';
import WallLiveItemPreview from './WallLiveItemPreview';

/**
 * Smart Template Engine — Preview System (Phase 3A): Wall Live Preview.
 *
 * Shows what the wall is showing RIGHT NOW, across all screens in grid layout, by
 * reading the live RTDB wall node and computing the current item client-side from
 * start_timestamp + per-item durations + serverTimeOffset (the device doesn't
 * report its item index).
 *
 * BUG 3 FIX — NO false positives: a device that left wall mode leaves stale
 * "playing" data in RTDB (the device doesn't clear it). So we DON'T trust `state`
 * alone — we require a FRESH per-screen `lastSync` heartbeat (≤5s). If no screen is
 * fresh, the wall isn't actually live. Per cell: a screen whose lastSync is stale is
 * shown as "not in wall mode" rather than fake content.
 *
 * Read-only / frontend-only — ZERO sync contact (no Android changes).
 */
const STALE_MS = 5000;

export default function WallLivePreview({ wallId, maxWidth = 560 }) {
  const { t } = useTranslation();
  const live = useWallLiveState(wallId);

  // The wall CLAIMS to be playing (per RTDB state) — but that can be stale.
  const claimedPlaying = live.state === 'playing' && live.startTimestamp > 0 && live.playlist.length > 0;

  const durationsMs = useMemo(
    () => live.playlist.map((it) => Number(it.duration_ms) || (Number(it.duration_seconds) || 10) * 1000),
    [live.playlist],
  );

  // Tick while the wall claims to play, so freshness re-evaluates even when stale.
  const { index } = useWallTimeline({
    durationsMs,
    getElapsed: () => live.serverNow() - live.startTimestamp,
    playing: claimedPlaying,
    loop: live.loop,
  });

  const rows = live.rows;
  const cols = live.columns;
  const grid = { rows, cols, orientation: live.orientation };
  const positions = useMemo(() => Array.from({ length: rows * cols }, (_, i) => i + 1), [rows, cols]);

  // Freshness — recomputed every tick (serverNow advances).
  const now = live.serverNow();
  const isFresh = (pos) => {
    const ls = Number(live.screens?.[String(pos)]?.lastSync) || 0;
    return ls > 0 && now - ls < STALE_MS;
  };
  const anyFresh = positions.some(isFresh);
  // What each (fresh) screen says it's actually showing — so a cell that reports an
  // error / stall is overlaid honestly instead of rendering the clock-derived content.
  const cellStatus = (pos) => (isFresh(pos) ? live.screens?.[String(pos)]?.content_status : null);
  const anyError = positions.some((p) => cellStatus(p) === 'no_content');

  // Not even claiming to play → idle/preparing/stopped.
  if (!claimedPlaying) {
    const msg = live.state === 'preparing' ? t('video_wall.live.preparing')
      : live.state === 'stopped' ? t('video_wall.live.stopped')
        : t('video_wall.live.idle');
    return <StatusCard icon="pi pi-desktop" msg={msg} />;
  }

  // Claims to play but NO device is reporting (stale ≥5s) → it left wall mode.
  if (!anyFresh) {
    return <StatusCard icon="pi pi-exclamation-triangle" tone="warn" msg={t('video_wall.live.stale_playing')} />;
  }

  const current = live.playlist[index];

  // Geometry mirrors WallLiveItemPreview so the per-cell overlay aligns exactly.
  const portrait = live.orientation === 'portrait';
  const cellAspect = portrait ? 9 / 16 : 16 / 9;
  const bezel = 4;
  const cellW = (maxWidth - (cols - 1) * bezel) / cols;
  const cellH = cellW / cellAspect;
  const staleCount = positions.filter((p) => !isFresh(p)).length;

  return (
    <div className="d-flex flex-column align-items-center gap-2">
      <div className="d-flex align-items-center gap-2 small" style={{ color: 'var(--pp-text-secondary)' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: anyError ? 'var(--pp-danger, #ef4444)' : 'var(--pp-success)', display: 'inline-block' }} />
        {t('video_wall.live.live')} · {t('video_wall.preview.item_n', { n: index + 1 })} / {live.playlist.length}
        {anyError && (
          <span style={{ color: 'var(--pp-danger, #ef4444)' }}>· {t('video_wall.screen_error')}</span>
        )}
        {staleCount > 0 && (
          <span style={{ color: 'var(--pp-warning)' }}>· {t('video_wall.live.some_stale', { n: staleCount })}</span>
        )}
      </div>

      <div style={{ position: 'relative', width: 'fit-content' }}>
        <WallLiveItemPreview item={current} grid={grid} maxWidth={maxWidth} />
        {/* per-cell stale overlay — covers only screens whose heartbeat went stale.
            dir="ltr" so it stays aligned with the LTR base grid under RTL. */}
        <div dir="ltr" style={{ position: 'absolute', inset: 0, display: 'grid',
          gridTemplateColumns: `repeat(${cols}, ${cellW}px)`, gridTemplateRows: `repeat(${rows}, ${cellH}px)`,
          gap: bezel, padding: bezel, pointerEvents: 'none' }}>
          {positions.map((pos) => {
            const stale = !isFresh(pos);
            const cs = cellStatus(pos);
            const err = cs === 'no_content';
            const buf = cs === 'buffering';
            const bg = stale ? 'rgba(0,0,0,0.82)'
              : err ? 'rgba(176,32,32,0.86)'
              : buf ? 'rgba(0,0,0,0.55)'
              : 'transparent';
            const msg = stale ? t('video_wall.live.not_in_wall')
              : err ? t('video_wall.screen_error')
              : buf ? t('video_wall.screen_buffering')
              : null;
            return (
              <div key={pos} style={{ borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                textAlign: 'center', color: '#fff', fontSize: 11, padding: 4, background: bg }}>
                {msg && (
                  <span><i className="pi pi-exclamation-triangle d-block mb-1" />{msg}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon, msg, tone }) {
  return (
    <div className="pp-card p-5 text-center" style={{ minWidth: 240 }}>
      <span className="pp-empty-icon mb-3" style={{ width: 52, height: 52, fontSize: '1.4rem',
        color: tone === 'warn' ? 'var(--pp-warning)' : undefined }}>
        <i className={icon} />
      </span>
      <p className="mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{msg}</p>
    </div>
  );
}
